import { CONVERSATION_STATUSES, MAX_THREAD_HISTORY } from "@repo/shared";
import type { AssistantConversationListItem, AssistantConversationStatus } from "../../types";

/**
 * What the conversations screen's filter bar holds, and the pure function that applies it.
 *
 * Client-side, over the whole list: `listProjectConversations` returns at most
 * `MAX_THREAD_HISTORY` (120) conversations in one shot, so there is nothing to page and
 * nothing a server-side predicate would save. The one filter that is *not* applied here is
 * "Show archived" — that one changes the query's own arguments (`includeArchived`), because an
 * archived conversation is not in the result to be filtered in.
 */

/**
 * The backend's own cap — the same constant `claudia.listProjectConversations` slices to, so the
 * footer's "this is everything we were given" cannot drift from what we were actually given.
 */
export const CONVERSATION_LIST_CAP = MAX_THREAD_HISTORY;

/** The window the Date filter narrows to. `all` is off. */
export type ConversationDateWindow = "today" | "7d" | "30d" | "all";

export type ConversationSortKey = "needs_you" | "recent" | "title";

export interface ConversationFilterState {
  searchQuery: string;
  /** `null` is "every status" — the `FilterPopover` convention. */
  statuses: Set<AssistantConversationStatus> | null;
  dateWindow: ConversationDateWindow;
  /** Passed to the query as `includeArchived`, not applied by `applyConversationFilters`. */
  showArchived: boolean;
  sort: ConversationSortKey;
}

export const EMPTY_CONVERSATION_FILTERS: ConversationFilterState = {
  searchQuery: "",
  statuses: null,
  dateWindow: "all",
  showArchived: false,
  // The reason someone opens this screen (design note `WcY67`).
  sort: "needs_you",
};

/** The three words the STATUS column and the Status filter both use. One vocabulary. */
export const CONVERSATION_STATUS_LABELS: Record<AssistantConversationStatus, string> = {
  working: "Working",
  needs_you: "Needs you",
  idle: "Idle",
};

/**
 * Where a status sits in the filter bar, as the design lists them.
 *
 * Keyed rather than hand-listed so a status added to the shared vocabulary is a compile error
 * here instead of an option the filter quietly never offers.
 */
const CONVERSATION_STATUS_DISPLAY_RANK: Record<AssistantConversationStatus, number> = {
  working: 0,
  needs_you: 1,
  idle: 2,
};

export const CONVERSATION_STATUS_ORDER: AssistantConversationStatus[] = [
  ...CONVERSATION_STATUSES,
].sort((a, b) => CONVERSATION_STATUS_DISPLAY_RANK[a] - CONVERSATION_STATUS_DISPLAY_RANK[b]);

export const CONVERSATION_DATE_LABELS: Record<ConversationDateWindow, string> = {
  today: "Today",
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  all: "All time",
};

export const CONVERSATION_SORT_LABELS: Record<ConversationSortKey, string> = {
  needs_you: "Needs you first",
  recent: "Recently updated",
  title: "Title A–Z",
};

const DAY_MS = 86_400_000;

/**
 * Is this conversation inside the chosen window?
 *
 * "Today" is the calendar day — a conversation touched at 9am is not "yesterday" at 8am the
 * next morning just because 24 hours have not passed. The other two are rolling windows, which
 * is what "last 7 days" says.
 */
function isWithinDateWindow(
  timestamp: number,
  window: ConversationDateWindow,
  now: number
): boolean {
  if (window === "all") return true;
  if (window === "today") {
    return new Date(timestamp).toDateString() === new Date(now).toDateString();
  }
  return now - timestamp <= (window === "7d" ? 7 : 30) * DAY_MS;
}

function matchesSearch(item: AssistantConversationListItem, query: string): boolean {
  if (query.length === 0) return true;
  return (
    item.title.toLowerCase().includes(query) ||
    (item.preview?.toLowerCase().includes(query) ?? false)
  );
}

/**
 * Where a status sorts under "Needs you first": the conversations waiting on a person, then
 * the ones still running, then everything settled.
 */
const STATUS_SORT_RANK: Record<AssistantConversationStatus, number> = {
  needs_you: 0,
  working: 1,
  idle: 2,
};

/**
 * Filter and order the conversation list.
 *
 * Pure, and `now` is an argument rather than a clock read, so the Date filter is testable as a
 * duration and a whole screen judges one moment.
 *
 * Every sort is applied to a copy with `Array.prototype.sort`, which is stable — so "Needs you
 * first" ranks by status and leaves the backend's pinned-first, activity-descending order
 * intact *inside* each rank rather than reshuffling it, which is the same rule
 * `partitionConversations` follows.
 */
export function applyConversationFilters(
  items: readonly AssistantConversationListItem[],
  filters: ConversationFilterState,
  now: number
): AssistantConversationListItem[] {
  const query = filters.searchQuery.trim().toLowerCase();
  const visible = items.filter(
    (item) =>
      matchesSearch(item, query) &&
      (filters.statuses === null || filters.statuses.has(item.status)) &&
      isWithinDateWindow(item.lastActivityAt, filters.dateWindow, now)
  );

  switch (filters.sort) {
    case "recent":
      return visible.sort((left, right) => right.lastActivityAt - left.lastActivityAt);
    case "title":
      return visible.sort((left, right) =>
        left.title.localeCompare(right.title, undefined, { sensitivity: "base" })
      );
    default:
      return visible.sort(
        (left, right) => STATUS_SORT_RANK[left.status] - STATUS_SORT_RANK[right.status]
      );
  }
}

/**
 * Is anything narrowing the list? The count beside the title says "N of M" only when it is.
 *
 * The sort is not a filter and never counts; "Show archived" *adds* rows rather than removing
 * them, so it does not either.
 */
export function isConversationFilterActive(filters: ConversationFilterState): boolean {
  return (
    filters.searchQuery.trim().length > 0 ||
    (filters.statuses !== null && filters.statuses.size > 0) ||
    filters.dateWindow !== "all"
  );
}
